import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useEnterWorkspace, useWorkspaces } from '../hooks/useWorkspaces'
import { hasMultipleWorkspaces, type Workspace } from '../services/workspaces.service'

interface WorkspaceSwitcherViewProps {
  workspaces: readonly Workspace[]
  current: string
  onEnter: (tenantId: string) => void
  onOpenOverview: () => void
  disabled?: boolean
}

/** Pure view: renders nothing unless there is more than one workspace. */
export const WorkspaceSwitcherView: React.FC<WorkspaceSwitcherViewProps> = ({
  workspaces,
  current,
  onEnter,
  onOpenOverview,
  disabled,
}) => {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  if (!hasMultipleWorkspaces(workspaces)) return null

  const active = workspaces.find((w) => w.tenantId === current)

  return (
    <div className="relative" ref={ref} data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('workspaces.switcher.label')}
        data-testid="workspace-switcher-trigger"
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-crypto-muted hover:text-crypto-text hover:bg-crypto-accent/10 transition-all"
      >
        <span className="max-w-[10rem] truncate">
          {active?.name ?? t('workspaces.switcher.label')}
        </span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          data-testid="workspace-switcher-menu"
          className="absolute right-0 mt-2 w-72 glass-effect rounded-xl p-2 border border-crypto-border/50 bg-crypto-card z-40"
        >
          {workspaces.map((w) => {
            const isCurrent = w.tenantId === current
            return (
              <button
                key={w.tenantId}
                type="button"
                role="option"
                aria-selected={isCurrent}
                disabled={isCurrent || disabled}
                onClick={() => onEnter(w.tenantId)}
                data-testid={`workspace-option-${w.tenantId}`}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-crypto-accent/10 disabled:cursor-default transition-all"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-sm text-crypto-text truncate">
                    {w.kind === 'own' ? t('workspaces.own') : w.name}
                  </span>
                  {isCurrent && (
                    <span className="text-xs text-crypto-accent">{t('workspaces.current')}</span>
                  )}
                </span>
                {w.kind === 'client' && (
                  <span className="block text-xs text-crypto-muted">
                    {t(`workspaces.roles.${w.role}`, { defaultValue: w.role })}
                    {w.expiresAt
                      ? ` · ${t('workspaces.expires', {
                          date: new Date(w.expiresAt).toLocaleDateString(),
                        })}`
                      : ''}
                  </span>
                )}
              </button>
            )
          })}
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onOpenOverview()
            }}
            data-testid="workspace-overview-link"
            className="w-full text-left px-3 py-2 mt-1 border-t border-crypto-border/50 text-sm text-crypto-accent hover:bg-crypto-accent/10 rounded-lg"
          >
            {t('workspaces.overview.link')}
          </button>
        </div>
      )}
    </div>
  )
}

/** Connected switcher for the navbar: hidden while there is a single workspace. */
export const WorkspaceSwitcher: React.FC<{ onOpenOverview: () => void }> = ({ onOpenOverview }) => {
  const { data } = useWorkspaces()
  const { enter, isEntering } = useEnterWorkspace()
  if (!data) return null
  return (
    <WorkspaceSwitcherView
      workspaces={data.workspaces}
      current={data.current}
      onEnter={enter}
      onOpenOverview={onOpenOverview}
      disabled={isEntering}
    />
  )
}
